import * as React from "react";
import {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {Inventory} from "./inventory";
import {
    Avatar,
    classNames,
    layout,
    MessageView,
    PaymentError,
    PushButton,
    theme,
    typography,
    useAsyncCallback,
    useAsyncEffect,
    useBoxLockStatus,
    useCountdown,
    useIdle,
    usePayment,
    usePlatform,
    usePreventDeactivate,
    View,
    ViewContent
} from "@variocube/cube-ui";
import {Product} from "./types";
import {LockStatus, LogLevel, Occupancy, Payment} from "@variocube/app-api";
import {VendingViewHeader} from "./header";
import {timeout} from "@variocube/driver-common";
import {stylesheet} from "typestyle";
import {createDeferredPromise} from "@variocube/app-common";
import {EmailView} from "./email";
import {logger} from "./logger";
import {useLocalization} from "../i18n";

interface VendViewProps {
    inventory: Inventory;
    product: Product;
    onDone: () => void;
}

const log = logger.sub("VendView");

export function VendView(props: VendViewProps) {
    const {inventory, product, onDone} = props;
    const {t} = useLocalization();

    const [vendedOccupancy, setVendedOccupancy] = useState<Occupancy>();
    const occupancy = useMemo(() => vendedOccupancy ?? inventory.getAnyOccupancy(product), [vendedOccupancy, inventory, product]);

    const [email, setEmail] = useState<string>();
    const [emailDone, setEmailDone] = useState(false);

    const handleEmail = useCallback((email: string) => {
        log.info("Customer e-mail", email);
        setEmail(email);
        setEmailDone(true);
    }, []);

    const handleEmailSkip = useCallback(() => setEmailDone(true), []);

    if (!occupancy) {
        // This should obviously not happen if the logic to enable/disable the buy button is correct.
        return (
            <MessageView message={t("OutOfStock")}>
                <PushButton onClick={onDone}>
                    {t("Back")}
                </PushButton>
            </MessageView>
        );
    }
    else if (!emailDone) {
        return (
            <EmailView onEmail={handleEmail} onSkip={handleEmailSkip}/>
        );
    }
    else {
        return (
            <Vend
                product={product}
                occupancy={occupancy}
                email={email}
                onCancel={onDone}
                onVendDone={setVendedOccupancy}
            />
        );
    }
}

interface VendProps {
    product: Product;
    occupancy: Occupancy;
    email?: string;
    onCancel: () => void;
    onVendDone: (occupancy: Occupancy) => void;
}

export function Vend({product, occupancy, email, onCancel, onVendDone}: VendProps) {
    const {t, language} = useLocalization();
    const platform = usePlatform();
    const lockStatus = useBoxLockStatus(occupancy.boxNumber);
    const [payment, setPayment] = useState<Payment>();

    const waitForLock = useMemo(() => createDeferredPromise<void>(), []);

    const handleVend = useCallback(async (payment: Payment) => {
        setPayment(payment);
        await platform.log(LogLevel.Audit, "Start vending", {payment, occupancy});
        await platform.openBoxLock(occupancy.boxNumber);
        try {
            // Wait for the lock to open with a timeout.
            // Valina seems to cancel after 7s of not confirming the payment, so we wait max. 5s here.
            // This obviously won't work with Rieber since we have much longer times to open the lock.
            return await Promise.race([waitForLock.promise, timeout(5000)]);
        }
        catch (error) {
            // timeout. check the lock status again to make sure we can cancel
            log.warn("Timeout waiting for box to open. Checking status again.");
            await platform.log(LogLevel.Audit, "Vending failed", {payment, occupancy});
            throw error;
        }
    }, [platform, occupancy]);

    useAsyncEffect(async () => {
        if (lockStatus == LockStatus.Open && payment) {
            log.info("Box open and payment approved. Completing vending.");
            waitForLock.resolve();
            onVendDone(occupancy);
            await platform.endOccupancy(occupancy.uuid, {
                content: {payment, customerEmail: email},
                merge: true,
            });
            await platform.log(LogLevel.Audit, "Complete vending", {payment, occupancy});
        }
    }, [lockStatus, payment, onVendDone, platform, occupancy, email]);

    const handleReopenBox = useAsyncCallback(async () => {
        log.info("Re-opening box.");
        await platform.openBoxLock(occupancy.boxNumber);
    }, [platform, occupancy]);

    const paymentOptions = useMemo(() => ({
        amount: product.price.gross,
        currency: "EUR",
        language: language,
        onVend: handleVend,
    }), [product, handleVend, language]);

    const {processing, message, error, retry, canRetry, cancel, canCancel} = usePayment(paymentOptions);

    const handleCancel = useCallback(async () => {
        log.info("Customer canceled vending.");
        await cancel();
        onCancel();
    }, [cancel, onCancel]);

    const errorMessage = useMemo(() => {
        switch (error) {
            case PaymentError.System:
                return t("PaymentErrorSystem");
            case PaymentError.Denied:
                return t("PaymentErrorDenied");
            case PaymentError.Vending:
                return t("PaymentErrorVending");
            case PaymentError.Confirmation:
                return t("PaymentErrorConfirmation");
            default:
                return undefined;
        }
    }, [error]);

    const open = lockStatus == LockStatus.Open;
    const vend = !processing && !error;
    const done = vend && !open;

    const seconds = useCountdown(20, done);
    useEffect(() => {
        if (seconds <= 0) {
            onCancel();
        }
    }, [seconds, onCancel]);

    const idle = useIdle(60);
    const closeIdle = useCountdown(20, idle);
    useEffect(() => {
        if (closeIdle <= 0) {
            onCancel();
        }
    }, [closeIdle, onCancel]);

    usePreventDeactivate();

    return (
        <View>
            <VendingViewHeader title={t("Payment")}/>
            <ViewContent className={classNames(layout.horizontal, layout.center, layout.alignCenter)}>
                <div
                    className={classNames(layout.vertical, layout.center, layout.alignCenter, layout.paddingMedium, styles.boxLeft)}>
                    <div className={classNames(layout.paddingMedium, typography.alignCenter, typography.title)}>
                        {t("Box")}
                    </div>
                    <Avatar primary={open} size={4}>
                        {occupancy.boxNumber}
                    </Avatar>
                    <div className={classNames(layout.paddingMedium, typography.alignCenter)}>
                        {product.name}
                    </div>
                </div>
                <div className={classNames(layout.paddingMedium, styles.boxRight)}>
                    <div className={typography.title}>
                        {vend ? t("VendDone") : t("VendProcessing")}
                    </div>
                    {processing && (
                        <div className={classNames(theme.moreContrast, layout.marginYMedium, layout.paddingMedium)}>
                            {message.map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </div>
                    )}
                    {errorMessage && (
                        <div className={classNames(layout.marginYMedium, typography.colorError)}>
                            {errorMessage}
                        </div>
                    )}
                    {canCancel && (
                        <div className={classNames(layout.marginYMedium, layout.horizontal, layout.spaceBetween)}>
                            <PushButton onClick={handleCancel}>
                                {t("Cancel")}
                            </PushButton>
                        </div>
                    )}
                    {canRetry && (
                        <div className={classNames(layout.marginYMedium, layout.horizontal, layout.spaceBetween)}>
                            <PushButton onClick={onCancel}>
                                {t("Cancel")}
                            </PushButton>
                            <PushButton primary onClick={retry}>
                                {t("Retry")}
                            </PushButton>
                        </div>
                    )}
                    {vend && !done && (
                        <div className={layout.marginYMedium}>
                            {t("VendRetrieveItem")}
                        </div>
                    )}
                    {done && (
                        <Fragment>
                            <div className={classNames(layout.marginYMedium, typography.muted)}>
                                {t("AutoCloseMessage", {seconds})}
                            </div>
                            <div className={classNames(layout.marginYMedium, layout.horizontal, layout.spaceBetween)}>
                                <PushButton onClick={handleReopenBox.execute}>
                                    {t("ReopenBox")}
                                </PushButton>
                                <PushButton primary onClick={onCancel}>
                                    {t("Close")}
                                </PushButton>
                            </div>
                        </Fragment>
                    )}
                    {idle && (
                        <div className={classNames(layout.marginYMedium, typography.muted)}>
                            {t("AutoCancelMessage", {seconds: closeIdle})}
                        </div>
                    )}
                </div>
            </ViewContent>
        </View>
    );
}

const styles = stylesheet({
    boxLeft: {
        width: "16em"
    },
    boxRight: {
        width: "28em"
    }
});